interface CoresMeterProps {
  /** Cores given out on this machine; `null` when nothing was measured. */
  used: number | null;
  /** Cores the machine really allocates; `null` when nothing was measured. */
  total: number | null;
  /** Accessible name, e.g. the machine these cores belong to. */
  label: string;
}

/**
 * How much of a machine is given out, as one bar.
 *
 * Deliberately **not** a square per core, the way the GPU screen paints a
 * square per device. A GPU square earns its place because a GPU is a thing
 * with an identity: it has a holder, an index a researcher can find again in
 * `nvidia-smi`, and its own measured activity. A core has none of those — the
 * console knows how many were promised to tasks and nothing else, so sixty-two
 * identical squares would be sixty-two repetitions of a single number, and
 * every one of them would invite the reading "this particular core is busy",
 * which is exactly what these figures do not say. Cores are a quantity, so
 * they are drawn as a quantity.
 *
 * Args:
 *     used: Cores given out.
 *     total: Cores the machine allocates.
 *     label: What the bar is about, for the accessible name.
 *
 * Returns:
 *     A meter, or nothing at all when the numbers were not measured.
 */
export function CoresMeter({ used, total, label }: CoresMeterProps): JSX.Element {
  if (used === null || total === null || total <= 0) {
    // No bar rather than an empty one: a bar at zero reads as "nothing is
    // running here", and the whole point of the unmeasured case is that the
    // console does not know that.
    return (
      <p className="util__unknown">
        Not measured, so nothing is shown here rather than a number that could
        be wrong.
      </p>
    );
  }

  const pct = Math.max(0, Math.min(100, (used / total) * 100));
  return (
    <div className="util cores-meter">
      <div
        className="util__track"
        role="meter"
        aria-label={`Cores given out on ${label}`}
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuetext={`${used} of ${total} cores given out`}
      >
        <div className="util__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="util__value mono">
        {used}/{total}
      </span>
    </div>
  );
}
